import { z } from 'zod';
import { avatarIdField } from '../profile/avatars.js';

/**
 * Contrats d'authentification (source unique — ancrage §5, spec 01 §4).
 * Règle : aucun DTO sortant n'expose de secret (secretHash, tokenHash, providerId local).
 */

/** Politique mot de passe (spec 03 §2/§7 : ≥ 12). */
export const AUTH_PASSWORD_MIN_LENGTH = 12;
export const AUTH_PASSWORD_MAX_LENGTH = 200;
/** displayName public : 3–20, alphanumérique + tiret/underscore. */
export const DISPLAY_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;
export const DISPLAY_NAME_MIN = 3;
export const DISPLAY_NAME_MAX = 20;
export const EMAIL_MAX_LENGTH = 320;

export const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .email('Hmm, cet email a une drôle de tête. Vérifie-le !')
  .max(EMAIL_MAX_LENGTH);
export const displayNameField = z
  .string()
  .trim()
  .min(DISPLAY_NAME_MIN, `Ton pseudo doit faire au moins ${DISPLAY_NAME_MIN} caractères !`)
  .max(DISPLAY_NAME_MAX, `Ton pseudo ne peut pas dépasser ${DISPLAY_NAME_MAX} caractères !`)
  .regex(DISPLAY_NAME_REGEX, 'Lettres, chiffres, tiret et underscore uniquement');
/** Mot de passe à la création (signup, changement) — politique spec 03 §2/§7. */
export const newPasswordField = z
  .string()
  .min(
    AUTH_PASSWORD_MIN_LENGTH,
    `Au moins ${AUTH_PASSWORD_MIN_LENGTH} caractères — un mot de passe costaud comme un panda !`,
  )
  .max(AUTH_PASSWORD_MAX_LENGTH);

export const SignupSchema = z
  .object({
    email: emailField,
    password: newPasswordField,
    displayName: displayNameField,
    // Avatar du catalogue, optionnel à l'inscription (spec 07 §2.5).
    avatarId: avatarIdField.optional(),
  })
  .strict();
export type SignupDto = z.infer<typeof SignupSchema>;

export const LoginSchema = z
  .object({
    email: emailField,
    password: z.string().min(1, 'N’oublie pas ton mot de passe !').max(AUTH_PASSWORD_MAX_LENGTH),
  })
  .strict();
export type LoginDto = z.infer<typeof LoginSchema>;

export const RefreshSchema = z
  .object({
    refreshToken: z.string().min(1).optional(),
  })
  .strict();
export type RefreshDto = z.infer<typeof RefreshSchema>;

/** Changement de mot de passe (spec 07 §3.2) — re-preuve du mot de passe courant. */
export const ChangePasswordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, 'Ton mot de passe actuel est requis.')
      .max(AUTH_PASSWORD_MAX_LENGTH),
    newPassword: newPasswordField,
  })
  .strict();
export type ChangePasswordDto = z.infer<typeof ChangePasswordSchema>;

/**
 * Re-preuve d'une action sensible (spec 07 §3 / spec 11 §7) — DUALE :
 * - compte avec mot de passe (`hasPassword=true`) → `currentPassword` (inchangé) ;
 * - compte **social-only** → `reauthProof` = jeton signé émis par le callback
 *   `start?intent=reauth` (TTL 5 min, lié au userId, usage unique). Côté WEB il vit
 *   dans le cookie httpOnly `rp_reauth` (le corps peut alors être vide) ; côté
 *   bearer/natif (§10) il est fourni dans le corps. Le serveur exige exactement un
 *   moyen valide selon `hasPassword` (autorité serveur).
 */
export const currentPasswordProofField = z
  .string()
  .min(1, 'Ton mot de passe actuel est requis.')
  .max(AUTH_PASSWORD_MAX_LENGTH)
  .optional();
export const reauthProofField = z.string().min(1).max(4096).optional();

/** « Mot de passe oublié » (spec 06 §5.5) — réponse toujours neutre côté serveur. */
export const PasswordForgotSchema = z
  .object({
    email: emailField,
  })
  .strict();
export type PasswordForgotDto = z.infer<typeof PasswordForgotSchema>;

/** Reset par token reçu par email (spec 06 §5.5) — même politique que le signup. */
export const PasswordResetSchema = z
  .object({
    token: z.string().min(1, 'Le lien est incomplet.').max(200),
    password: newPasswordField,
  })
  .strict();
export type PasswordResetDto = z.infer<typeof PasswordResetSchema>;

/**
 * Changement d'email (spec 07 §3.3) — l'ancien reste actif tant que le nouveau n'est
 * pas vérifié. Re-preuve DUALE (spec 11 §7) : `currentPassword` OU `reauthProof`
 * (compte social-only) — le serveur tranche selon `hasPassword` ; côté web le
 * `reauthProof` arrive par cookie httpOnly (corps sans preuve accepté par le zod).
 */
export const ChangeEmailSchema = z
  .object({
    newEmail: emailField,
    currentPassword: currentPasswordProofField,
    reauthProof: reauthProofField,
  })
  .strict();
export type ChangeEmailDto = z.infer<typeof ChangeEmailSchema>;

/**
 * Suppression du compte (spec 07 §3.6) — anonymisation côté serveur. Re-preuve
 * DUALE (spec 11 §7) : `currentPassword` OU `reauthProof` (compte social-only) —
 * la suppression doit rester possible SANS forcer la création d'un mot de passe
 * (RGPD + App Store). Web : `reauthProof` par cookie httpOnly `rp_reauth`.
 */
export const DeleteAccountSchema = z
  .object({
    currentPassword: currentPasswordProofField,
    reauthProof: reauthProofField,
  })
  .strict();
export type DeleteAccountDto = z.infer<typeof DeleteAccountSchema>;

export type AuthProviderName = 'local' | 'google' | 'meta' | 'firebase' | 'apple';
export type UserStatusName = 'active' | 'banned';

/**
 * Providers de connexion SOCIALE (spec 11) — sous-ensemble « externe » d'
 * `AuthProviderName` (ni `local`, ni `firebase` réservé). Ordre = ordre d'affichage.
 */
export const SOCIAL_PROVIDERS = ['google', 'apple', 'meta'] as const;
export type SocialProviderName = (typeof SOCIAL_PROVIDERS)[number];

/** Utilisateur exposé au client — vue de SOI uniquement (jamais de secret). */
export interface PublicUserDto {
  id: string;
  email: string;
  emailVerified: boolean;
  /** Nouvelle adresse en attente de vérification (spec 07 §3.3), sinon null. */
  pendingEmail: string | null;
  displayName: string;
  avatarId: string | null;
  status: UserStatusName;
  /**
   * Le compte a-t-il un mot de passe local (identité `local` avec `secretHash`) ?
   * Pilote « Définir vs Changer le mot de passe », DangerZone et les garde-fous de
   * déliaison (spec 11 §3/§7). Un compte social-only vaut `false`.
   */
  hasPassword: boolean;
}

/**
 * Session émise. Web : jetons posés en cookies (absents du corps).
 * Bearer (mobile) : jetons présents dans le corps, aucun Set-Cookie.
 */
export interface AuthSessionDto {
  user: PublicUserDto;
  accessToken?: string;
  refreshToken?: string;
  /** Durée de vie de l'access token en secondes. */
  accessExpiresIn: number;
}

// ---------------------------------------------------------------------------
// Connexion sociale (spec 11, ADR 0020) — contrats §3. Les FLUX (routes,
// stratégies, tickets signés) arrivent aux lots 11b→11e ; ici, seuls les DTO
// partagés et les schémas de validation d'entrée.
// ---------------------------------------------------------------------------

/** Providers sociaux ACTIFS (sonde publique `GET /auth/providers`, spec 11 §12). */
export interface AuthProvidersDto {
  providers: SocialProviderName[];
}

/**
 * Identité liée, exposée dans les réglages « Comptes connectés » (spec 11 §6).
 * JAMAIS le `sub`/`providerId` ni de secret — `email` est le snapshot informatif
 * (peut être masqué à l'affichage), `null` si le provider n'en a pas fourni.
 */
export interface LinkedIdentityDto {
  provider: SocialProviderName;
  email: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Données d'onboarding du signup social (spec 11 §5) — lues d'un ticket signé
 * (cookie `rp_signup`), le callback n'ayant rien écrit en base (création différée).
 */
export interface SocialPendingDto {
  provider: SocialProviderName;
  email: string | null;
  /** Dérivé du `profileHint` provider, nettoyé via la regex pseudo (pré-rempli). */
  suggestedDisplayName: string;
}

/** Finalise le signup social (spec 11 §5) : l'utilisateur CHOISIT son pseudo public. */
export const CompleteSocialSignupSchema = z
  .object({
    displayName: displayNameField,
    avatarId: avatarIdField.optional(),
  })
  .strict();
export type CompleteSocialSignupDto = z.infer<typeof CompleteSocialSignupSchema>;

/**
 * Confirmation de liaison — cas (c) de la machine de décision (spec 11 §4.2) :
 * l'utilisateur prouve son mot de passe. `ticket` optionnel = mode bearer (§10,
 * corps) ; côté web, il vit dans le cookie httpOnly `rp_link`.
 */
export const LinkConfirmSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, 'Ton mot de passe actuel est requis.')
      .max(AUTH_PASSWORD_MAX_LENGTH),
    ticket: z.string().min(1).max(4096).optional(),
  })
  .strict();
export type LinkConfirmDto = z.infer<typeof LinkConfirmSchema>;

/**
 * Token exchange NATIF (Capacitor, spec 11 §10 — contrat figé, implémentation
 * phase 4). Google/Apple → `idToken` (+`nonce` anti-rejeu) vérifié via JWKS ;
 * Meta → `accessToken` vérifié via `debug_token`. Union exclusive (`.strict()`).
 */
export const SocialTokenLoginSchema = z.union([
  z.object({ idToken: z.string().min(1), nonce: z.string().min(1).max(512).optional() }).strict(),
  z.object({ accessToken: z.string().min(1) }).strict(),
]);
export type SocialTokenLoginDto = z.infer<typeof SocialTokenLoginSchema>;

/**
 * Catalogue d'erreurs de la connexion sociale (spec 11 §3) — corps `{ error: code }`.
 * `provider_disabled` n'y figure pas : provider OFF = **404** (patron flag).
 */
export type SocialAuthErrorCode =
  | 'oauth_state_invalid'
  | 'oauth_exchange_failed'
  | 'provider_email_missing'
  | 'provider_email_unverified'
  | 'identity_already_linked'
  | 'link_confirmation_required'
  | 'signup_ticket_invalid'
  | 'last_login_method'
  | 'password_not_set'
  // Action sensible d'un compte social-only sans preuve de reauth valide (§7).
  | 'reauth_required';
